import React, { useState } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Typography,
  Link,
  Grid,
  Card,
  CardContent,
  Box,
  IconButton,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Button,
  alpha,
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Language,
  Twitter,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
  Share,
  CopyAll,
  OpenInNew,
  Schedule,
} from '@mui/icons-material';

function ContactUs() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Couleurs du logo
  const logoColors = {
    primary: '#008D36',
    secondary: '#E30613',
  };

  const contactInfo = {
    address: "INSBU, Zone Mukaza, Quartier Rohero, Avenue de l'Aviation, N°06, B.P. 1156 BUJUMBURA",
    phones: ["(+257) 22 21 67 34", "22 21 67 35", "22 22 21 49", "22 22 67 29", "22 22 26 35", "22 28 10 63"],
    emails: ["ins.burundi@insbu.bi", "ins.burundi2022@gmail.com"],
    website: "http://www.insbu.bi",
    twitter: "https://twitter.com/INSBURUNDI",
    facebook: "https://www.facebook.com/profile.php?id=61581042730925",
    linkedIn: "https://www.linkedin.com/in/ins-burundi-70a97237b/",
    whatsApp: "https://whatsapp.com/channel/0029VaIPXsuG3R3eWCaadr2n",
    openData: { name: 'Burundi OPEN DATA', url: 'https://burundi.opendataforafrica.org/' },
    maps: "https://www.google.pl/maps/place/Institut+de+Statistiques+et+d'Etudes+Economiques+du+Burundi+(ISTEEBU)/@-3.384871,29.3706578,18z"
  };

  const openingHours = {
    weekdays: "7h30 - 15h30",
    closed: "Samedi et Dimanches et jours fériés"
  };

  const handleCopyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        message: `${type} copié dans le presse-papier !`,
        severity: 'success'
      });
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'INSBU - Contact',
          text: 'Contactez l\'Institut National de la Statistique du Burundi',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Erreur de partage:', error);
      }
    } else {
      handleCopyToClipboard(window.location.href, 'Lien');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const ContactCard = ({ icon, title, children, action, color = logoColors.primary }) => (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s ease',
        border: `1px solid ${alpha(color, 0.1)}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${alpha(color, 0.15)}`,
          border: `1px solid ${alpha(color, 0.3)}`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              backgroundColor: alpha(color, 0.1),
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color: color } })}
          </Box>
          <Typography variant="h6" sx={{ ml: 2, fontWeight: 'bold', color: color }}>
            {title}
          </Typography>
          {action && (
            <Box sx={{ ml: 'auto' }}>
              {action}
            </Box>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  const ContactChips = ({ items, onCopy, type, color = logoColors.primary }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
      {items.map((item, index) => (
        <Chip
          key={index}
          label={item}
          variant="outlined"
          onClick={() => onCopy?.(item, type)}
          deleteIcon={<CopyAll fontSize="small" />}
          onDelete={() => onCopy?.(item, type)}
          sx={{ 
            cursor: 'pointer',
            borderColor: alpha(color, 0.3),
            color: color,
            '&:hover': {
              backgroundColor: alpha(color, 0.1),
              borderColor: color,
            }
          }}
        />
      ))}
    </Box>
  );

  const GradientButton = ({ children, onClick, startIcon, color = logoColors.primary }) => (
    <Button
      variant="contained"
      onClick={onClick}
      startIcon={startIcon}
      sx={{
        background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.8)})`,
        color: 'white',
        fontWeight: 'bold',
        px: 3,
        py: 1,
        borderRadius: 2,
        boxShadow: `0 4px 15px ${alpha(color, 0.3)}`,
        '&:hover': {
          background: `linear-gradient(135deg, ${alpha(color, 0.9)}, ${alpha(color, 0.7)})`,
          boxShadow: `0 6px 20px ${alpha(color, 0.4)}`,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.3s ease',
      }}
    >
      {children}
    </Button>
  );

  return (
    <Box sx={{ 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      <NavigationBar />
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* En-tête avec bouton de partage */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          mb: 6,
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3
        }}>
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography 
              variant="h2" 
              gutterBottom 
              sx={{ 
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: '#008D36',
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' }
              }}
            >
              Contactez-nous
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                maxWidth: '600px',
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              Pour toute demande d'information, n'hésitez pas à nous contacter via les différents canaux ci-dessous.
            </Typography>
          </Box>
          
          <GradientButton onClick={handleShare} startIcon={<Share />}>
            Partager
          </GradientButton>
        </Box>

        <Grid container spacing={3}>
          {/* Adresse */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<LocationOn />}
              title="Adresse"
              action={
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.address, 'Adresse')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
              }
            >
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {contactInfo.address}
              </Typography>
            </ContactCard>
          </Grid>

          {/* Téléphones */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<Phone />}
              title="Contacts Téléphoniques"
            >
              <ContactChips 
                items={contactInfo.phones} 
                onCopy={handleCopyToClipboard}
                type="Téléphone"
              />
            </ContactCard>
          </Grid>

          {/* Emails */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<Email />}
              title="E-Mail"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {contactInfo.emails.map((email, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Link 
                      href={`mailto:${email}`}
                      sx={{ 
                        textDecoration: 'none',
                        color: logoColors.primary,
                        fontWeight: 'medium',
                        '&:hover': { 
                          textDecoration: 'underline',
                          color: logoColors.secondary 
                        }
                      }}
                    >
                      {email}
                    </Link>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopyToClipboard(email, 'Email')}
                      sx={{ color: logoColors.primary }}
                    >
                      <CopyAll fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </ContactCard>
          </Grid>

          {/* Site Web */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<Language />}
              title="Site Web"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.primary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: logoColors.secondary 
                    }
                  }}
                >
                  {contactInfo.website.replace('http://', '')}
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.website, 'Site web')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.primary, opacity: 0.7 }} />
              </Box>
            </ContactCard>
          </Grid>

          {/* Burundi OPEN DATA */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<Language />}
              title="Plateforme Open Data"
              color={logoColors.secondary}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.openData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.secondary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: alpha(logoColors.secondary, 0.8)
                    }
                  }}
                >
                  {contactInfo.openData.name}
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.openData.url, 'Lien Open Data')}
                  sx={{ color: logoColors.secondary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.secondary, opacity: 0.7 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Accédez à nos données statistiques ouvertes
              </Typography>
            </ContactCard>
          </Grid>

          {/* Twitter */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<Twitter />}
              title="Suivez-nous sur Twitter"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.primary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: logoColors.secondary 
                    }
                  }}
                >
                  @INSBURUNDI
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.twitter, 'Lien Twitter')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.primary, opacity: 0.7 }} />
              </Box>
            </ContactCard>
          </Grid>

          {/* Facebook */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<FacebookIcon />}
              title="Suivez-nous sur Facebook"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.primary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: logoColors.secondary 
                    }
                  }}
                >
                  Institut National de la Statistique du Burundi
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.facebook, 'Lien Facebook')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.primary, opacity: 0.7 }} />
              </Box>
            </ContactCard>
          </Grid>

          {/* LinkedIn */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<LinkedInIcon />}
              title="Connectez-vous sur LinkedIn"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.primary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: logoColors.secondary 
                    }
                  }}
                >
                  INS Burundi
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.linkedIn, 'Lien LinkedIn')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.primary, opacity: 0.7 }} />
              </Box>
            </ContactCard>
          </Grid>

          {/* WhatsApp */}
          <Grid item xs={12} md={6}>
            <ContactCard
              icon={<WhatsAppIcon />}
              title="Rejoignez notre chaîne WhatsApp"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Link 
                  href={contactInfo.whatsApp}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textDecoration: 'none',
                    color: logoColors.primary,
                    fontWeight: 'medium',
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: logoColors.secondary 
                    }
                  }}
                >
                  Chaîne officielle INS Burundi
                </Link>
                <IconButton 
                  size="small" 
                  onClick={() => handleCopyToClipboard(contactInfo.whatsApp, 'Lien WhatsApp')}
                  sx={{ color: logoColors.primary }}
                >
                  <CopyAll fontSize="small" />
                </IconButton>
                <OpenInNew fontSize="small" sx={{ color: logoColors.primary, opacity: 0.7 }} />
              </Box>
            </ContactCard>
          </Grid>

          {/* Google Maps */}
          <Grid item xs={12}>
            <ContactCard
              icon={<LocationOn />}
              title="Localisation sur Google Maps"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                    Retrouvez-nous facilement grâce à Google Maps
                  </Typography>
                  <Link
                    href={contactInfo.maps}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textDecoration: 'none',
                      color: logoColors.primary,
                      fontWeight: 'medium',
                      '&:hover': { 
                        textDecoration: 'underline',
                        color: logoColors.secondary 
                      }
                    }}
                  >
                    Voir sur Google Maps
                  </Link>
                </Box>
                <GradientButton 
                  onClick={() => window.open(contactInfo.maps, '_blank')}
                  startIcon={<LocationOn />}
                >
                  Ouvrir la carte
                </GradientButton>
              </Box>
            </ContactCard>
          </Grid>
        </Grid>

        {/* Section Horaires */}
        <Box sx={{ mt: 6 }}>
          <Card 
            elevation={2}
            sx={{
              border: `2px solid ${alpha(logoColors.primary, 0.1)}`,
              background: `linear-gradient(135deg, ${alpha(logoColors.primary, 0.05)}, ${alpha(logoColors.secondary, 0.05)})`
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex' }}>
                <Schedule sx={{ color: logoColors.primary, mr: 2, fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: logoColors.primary }}>
                  Horaires d'ouverture
                </Typography>
              </Box>
              <Divider sx={{ my: 3, borderColor: alpha(logoColors.primary, 0.2) }} />
              <Grid container spacing={4}>
                <Grid item xs={12} md={12}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h5" gutterBottom sx={{ color: logoColors.primary, fontWeight: 'bold' }}>
                      Du lundi au vendredi
                    </Typography>
                    <Typography variant="h4" sx={{ color: logoColors.secondary, fontWeight: 'bold' }}>
                      {openingHours.weekdays}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 2,
                    backgroundColor: alpha(logoColors.secondary, 0.1),
                    borderRadius: 2,
                    border: `1px solid ${alpha(logoColors.secondary, 0.2)}`
                  }}>
                    <Typography variant="h6" sx={{ color: logoColors.secondary, fontStyle: 'italic' }}>
                      Fermé les {openingHours.closed}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Container>

      <Footer />

      {/* Snackbar de notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            background: `linear-gradient(135deg, ${logoColors.primary}, ${logoColors.secondary})`,
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ContactUs;